import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@clerk/clerk-react';
import axios from '../config/api';

interface APIKey {
  id: string;
  name: string;
  key_prefix: string;
  created_at: string;
  last_used_at: string | null;
}

interface UseApiKeysState {
  keys: APIKey[];
  isLoading: boolean;
  refresh: () => void;
  createKey: (name: string) => Promise<string | null>;
  revokeKey: (keyId: string) => Promise<boolean>;
}

export const useApiKeys = (): UseApiKeysState => {
  const [keys, setKeys] = useState<APIKey[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { getToken, isSignedIn, isLoaded } = useAuth();

  const fetchKeys = useCallback(async () => {
    if (!isLoaded || !isSignedIn) {
      setKeys([]);
      setIsLoading(false);
      return;
    }

    try {
      const token = await getToken();
      if (!token) return;

      const response = await axios.get('/api/keys', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setKeys(response.data);
    } catch (error) {
      console.error('Failed to fetch API keys:', error);
    } finally {
      setIsLoading(false);
    }
  }, [getToken, isSignedIn, isLoaded]);

  useEffect(() => {
    fetchKeys();
  }, [fetchKeys]);

  const createKey = useCallback(async (name: string): Promise<string | null> => {
    try {
      const token = await getToken();
      if (!token) return null;

      const response = await axios.post('/api/keys', { name }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await fetchKeys();
      return response.data.key; // raw key, shown only once
    } catch (error) {
      console.error('Failed to create API key:', error);
      return null;
    }
  }, [getToken, fetchKeys]);

  const revokeKey = useCallback(async (keyId: string): Promise<boolean> => {
    try {
      const token = await getToken();
      if (!token) return false;

      await axios.delete(`/api/keys/${keyId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await fetchKeys();
      return true;
    } catch (error) {
      console.error('Failed to revoke API key:', error);
      return false;
    }
  }, [getToken, fetchKeys]);

  return { keys, isLoading, refresh: fetchKeys, createKey, revokeKey };
};
